import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCw, Trash2, ShieldCheck, UserPlus, X, Copy, Loader2, UserMinus, UserCheck, Camera, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Select } from "@/components/FormBits";
import { Avatar } from "@/components/Avatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [tempPwReveal, setTempPwReveal] = useState(null); // { email, temp_password }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new temporary password will be emailed to them.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPwReveal({ email: u.email, temp_password: data.temp_password });
      fetchUsers();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const toggleDisable = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Account enabled" : "Account disabled");
      fetchUsers();
    } catch (e) { toast.error("Could not update user"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`Role set to ${role}`);
      fetchUsers();
    } catch (e) { toast.error("Could not change role"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone. Their projects will remain but become unowned.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  const uploadPhoto = async (u, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/users/${u.id}/avatar`, fd);
      toast.success(`Photo updated for ${u.name || u.email}`);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    }
  };

  const removePhoto = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}/avatar`);
      toast.success("Photo removed");
      fetchUsers();
    } catch { toast.error("Could not remove photo"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-users-page">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/admin" className="rcw-btn-ghost text-sm mb-3">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900">Users</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create staff accounts. New users receive a welcome email with a temporary password.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {users.some((u) => u.must_change_password && !u.disabled) && (
              <button onClick={() => setShowInvite(true)} className="rcw-btn-ghost" data-testid="users-invite-all-btn">
                <Send className="h-4 w-4" /> Invite all
                <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                  {users.filter((u) => u.must_change_password && !u.disabled).length}
                </span>
              </button>
            )}
            <button onClick={() => setShowCreate(true)} className="rcw-btn-amber" data-testid="users-create-btn">
              <UserPlus className="h-4 w-4" /> Add user
            </button>
          </div>
        </div>

        <div className="rcw-card overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">No users yet.</td></tr>
                )}
                {users.map((u) => {
                  const isSelf = me?.id === u.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2.5">
                          <PhotoCell user={u} onUpload={uploadPhoto} onRemove={removePhoto} />
                          <span>{u.name || "—"} {isSelf && <span className="text-xs text-gray-400">(you)</span>}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          disabled={isSelf}
                          className="rcw-input text-xs py-1"
                          data-testid={`user-role-${u.email}`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {u.must_change_password ? (
                          u.invited_at ? (
                            <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700" data-testid={`user-invited-${u.email}`}>
                              Invited {new Date(u.invited_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — not signed in yet
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700">Not invited yet</span>
                          )
                        ) : u.disabled ? (
                          <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700">Disabled</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => resetPw(u)} className="rcw-btn-ghost text-xs" data-testid={`user-reset-${u.email}`} title="Reset password">
                            <RefreshCw className="h-3.5 w-3.5" /> Reset
                          </button>
                          {!isSelf && (
                            <>
                              <button onClick={() => toggleDisable(u)} className="rcw-btn-ghost text-xs" data-testid={`user-toggle-${u.email}`}>
                                {u.disabled ? <><UserCheck className="h-3.5 w-3.5" /> Enable</> : <><UserMinus className="h-3.5 w-3.5" /> Disable</>}
                              </button>
                              <button onClick={() => removeUser(u)} className="rcw-btn-ghost text-xs text-red-600 hover:bg-red-50" data-testid={`user-delete-${u.email}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={(u) => { setTempPwReveal({ email: u.email, temp_password: u.temp_password }); fetchUsers(); }} />}
      {showInvite && (
        <InviteModal
          awaiting={users.filter((u) => u.must_change_password && !u.disabled)}
          onClose={() => { setShowInvite(false); fetchUsers(); }}
        />
      )}
      {tempPwReveal && <TempPwModal data={tempPwReveal} onClose={() => setTempPwReveal(null)} />}
    </Layout>
  );
}

function PhotoCell({ user, onUpload, onRemove }) {
  const ref = useRef(null);
  return (
    <div className="relative group shrink-0">
      <Avatar name={user.name} email={user.email} src={user.avatar_url} size="md" data-testid={`user-avatar-${user.email}`} />
      <button
        onClick={() => ref.current?.click()}
        className="absolute inset-0 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity flex items-center justify-center"
        title={user.avatar_url ? "Change photo (right-click to remove)" : "Add photo"}
        onContextMenu={(e) => { e.preventDefault(); if (user.avatar_url) onRemove(user); }}
        data-testid={`user-photo-btn-${user.email}`}
      >
        <Camera className="h-3.5 w-3.5" />
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => { onUpload(user, e.target.files?.[0]); e.target.value = ""; }}
        data-testid={`user-photo-input-${user.email}`} />
    </div>
  );
}

function InviteModal({ awaiting, onClose }) {
  const [checked, setChecked] = useState(() => new Set(awaiting.map((u) => u.id)));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/invite", { user_ids: Array.from(checked) }, { timeout: 120000 });
      setResults(data);
      if (data.failed === 0) toast.success(`${data.sent} invite${data.sent === 1 ? "" : "s"} sent`);
      else toast.warning(`${data.sent} sent, ${data.failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invites");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Invite awaiting team members">
      {results ? (
        <div data-testid="invite-results">
          <p className="text-sm text-gray-700 mb-3">
            <b>{results.sent}</b> invite{results.sent === 1 ? "" : "s"} sent{results.failed ? <>, <b className="text-red-600">{results.failed}</b> failed</> : ""}.
          </p>
          <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100 text-sm">
            {results.results.map((r) => (
              <li key={r.id} className="py-2 flex items-center gap-2">
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> : <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />}
                <span className="truncate">{r.email}</span>
                {!r.ok && <span className="text-xs text-red-500 truncate">— {r.error}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex justify-end">
            <button onClick={onClose} className="rcw-btn-primary" data-testid="invite-done-btn">Done</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-700">
            Each selected person gets a welcome email with a <b>fresh temporary password</b> and a sign-in link.
            They'll set their own password on first sign-in.
          </p>
          <ul className="mt-4 max-h-60 overflow-y-auto divide-y divide-gray-100" data-testid="invite-user-list">
            {awaiting.map((u) => (
              <li key={u.id} className="py-2 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={checked.has(u.id)}
                  onChange={() => toggle(u.id)}
                  className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                  data-testid={`invite-check-${u.email}`}
                />
                <Avatar name={u.name} email={u.email} src={u.avatar_url} size="xs" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{u.name || "—"}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={onClose} className="rcw-btn-ghost" data-testid="invite-cancel-btn">Cancel</button>
            <button onClick={send} disabled={busy || checked.size === 0} className="rcw-btn-amber" data-testid="invite-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send {checked.size} invite{checked.size === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { email: email.trim(), name: name.trim(), role });
      toast.success(`Invite sent to ${data.email}`);
      onCreated(data);
      onClose();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create user");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add a new user">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required data-testid="create-user-email" />
        </Field>
        <Field label="Full name">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-user-name" placeholder="e.g. Sam Davies" />
        </Field>
        <Field label="Role" required>
          <Select value={role} onChange={(e) => setRole(e.target.value)} options={["user", "admin"]} data-testid="create-user-role" />
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="text-xs text-gray-500">
          A temporary password will be generated and emailed to the user. They'll be asked to change it on first sign-in.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rcw-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="rcw-btn-amber" data-testid="create-user-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create user
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TempPwModal({ data, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(data.temp_password);
    toast.success("Copied to clipboard");
  };
  return (
    <Modal onClose={onClose} title="Temporary password">
      <p className="text-sm text-gray-700">
        We've emailed <b>{data.email}</b> a welcome message with this temporary password.
        You can also share it directly:
      </p>
      <div className="mt-4 flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono break-all" data-testid="temp-pw-value">
          {data.temp_password}
        </code>
        <button onClick={copy} className="rcw-btn-ghost" data-testid="temp-pw-copy">
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
      <div className="mt-4 text-xs text-gray-500">
        For security, this password is only shown once. They'll be asked to change it on first sign-in.
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={onClose} className="rcw-btn-primary">Done</button>
      </div>
    </Modal>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 sm:p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="rcw-label">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
